import {z} from 'zod'

export const pricingTierSchema = z.object({
    tierOrder: z.number().min(1, 'validation.tierOrder.min'),
    fromUnit: z.number().min(1, 'validation.fromUnit.min'),
    toUnit: z.number().nullable().optional(),
    pricePerUnit: z.number().min(0, 'validation.pricePerUnit.min'),
    unitType: z.literal('HOUR')
})

export const createPricingSchemeSchema = z.object({
    name: z.string()
        .min(1, 'validation.name.required')
        .max(255, 'validation.name.maxLength'),
    description: z.string().max(1000, 'validation.description.maxLength').optional(),
    modelType: z.literal('HOURLY_TIERED'),
    validFrom: z.string().min(1, 'validation.validFrom.required')
        .refine(
            (dateString) => {
                const date = new Date(dateString)
                const now = new Date()
                now.setHours(0, 0, 0, 0) // Start of today
                return date >= now
            },
            'validation.validFrom.mustBeFutureOrToday'
        ),
    tiers: z.array(pricingTierSchema)
        .min(1, 'validation.tiers.min')
        .refine(
            (tiers) => {
                // Check that tier orders are sequential starting from 1
                const orders = tiers.map(t => t.tierOrder).sort((a, b) => a - b)
                return orders.every((order, index) => order === index + 1)
            },
            'validation.tiers.orderSequential'
        )
        .refine(
            (tiers) => {
                // Check that tiers don't overlap
                const sortedTiers = [...tiers].sort((a, b) => a.tierOrder - b.tierOrder)
                for (let i = 1; i < sortedTiers.length; i++) {
                    const prevTier = sortedTiers[i - 1]
                    const currentTier = sortedTiers[i]

                    // If previous tier has a toUnit, it must not overlap with current tier's fromUnit
                    // For example: tier 1 goes 1-1, tier 2 goes 2-4 is valid
                    // But tier 1 goes 1-2, tier 2 goes 2-4 would overlap at unit 2
                    if (prevTier.toUnit !== null && prevTier.toUnit !== undefined) {
                        if (prevTier.toUnit >= currentTier.fromUnit) {
                            return false
                        }
                        // Also check that tiers are consecutive (no gaps)
                        if (prevTier.toUnit + 1 !== currentTier.fromUnit) {
                            return false
                        }
                    } else {
                        // Previous tier is unlimited (toUnit is null), so no further tiers allowed
                        return false
                    }
                }
                return true
            },
            {
                message: 'validation.tiers.noOverlap'
            }
        )
})

export const updatePricingSchemeSchema = z.object({
    id: z.string().min(1, 'validation.id.required'),
    name: z.string()
        .min(1, 'validation.name.required')
        .max(255, 'validation.name.maxLength'),
    description: z.string().max(1000, 'validation.description.maxLength').optional(),
    modelType: z.literal('HOURLY_TIERED'),
    validFrom: z.string().min(1, 'validation.validFrom.required')
        .refine(
            (dateString) => {
                const date = new Date(dateString)
                const now = new Date()
                now.setHours(0, 0, 0, 0) // Start of today
                return date >= now
            },
            'validation.validFrom.mustBeFutureOrToday'
        ),
    tiers: z.array(pricingTierSchema)
        .min(1, 'validation.tiers.min')
        .refine(
            (tiers) => {
                // Check that tier orders are sequential starting from 1
                const orders = tiers.map(t => t.tierOrder).sort((a, b) => a - b)
                return orders.every((order, index) => order === index + 1)
            },
            'validation.tiers.orderSequential'
        )
        .refine(
            (tiers) => {
                // Check that tiers don't overlap
                const sortedTiers = [...tiers].sort((a, b) => a.tierOrder - b.tierOrder)
                for (let i = 1; i < sortedTiers.length; i++) {
                    const prevTier = sortedTiers[i - 1]
                    const currentTier = sortedTiers[i]

                    // If previous tier has a toUnit, it must not overlap with current tier's fromUnit
                    // For example: tier 1 goes 1-1, tier 2 goes 2-4 is valid
                    // But tier 1 goes 1-2, tier 2 goes 2-4 would overlap at unit 2
                    if (prevTier.toUnit !== null && prevTier.toUnit !== undefined) {
                        if (prevTier.toUnit >= currentTier.fromUnit) {
                            return false
                        }
                        // Also check that tiers are consecutive (no gaps)
                        if (prevTier.toUnit + 1 !== currentTier.fromUnit) {
                            return false
                        }
                    } else {
                        // Previous tier is unlimited (toUnit is null), so no further tiers allowed
                        return false
                    }
                }
                return true
            },
            {
                message: 'validation.tiers.noOverlap'
            }
        )
})

export type CreatePricingSchemeFormData = z.infer<typeof createPricingSchemeSchema>
export type UpdatePricingSchemeFormData = z.infer<typeof updatePricingSchemeSchema>
export type PricingTierFormData = z.infer<typeof pricingTierSchema>
